import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Wifi, KeyRound, Upload, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WithTooltip } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ESTADO_BADGE = {
  mock: { label: "Modo demo (mock)", cls: "bg-slate-100 text-slate-600" },
  sandbox: { label: "Sandbox", cls: "bg-amber-100 text-amber-700" },
  produccion: { label: "Producción", cls: "bg-forest-600/15 text-forest-700" },
  "no configurado": { label: "No configurado", cls: "bg-slate-100 text-slate-500" },
};

export function CredencialDialog({ proveedor, open, onOpenChange, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ modo: "mock", base_url_sandbox: "", base_url_produccion: "", timeout: 30, reintentos: 2 });
  const [secretos, setSecretos] = useState({});
  const [publicf, setPublicf] = useState({});
  const [saving, setSaving] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState(null);
  const [puiInfo, setPuiInfo] = useState(null);

  useEffect(() => {
    if (!open || !proveedor) return;
    setData(null); setSecretos({}); setPrueba(null); setPuiInfo(null);
    api.get(`/integraciones/credenciales/${proveedor}`).then(({ data }) => {
      setData(data);
      setForm({ modo: data.modo, base_url_sandbox: data.base_url_sandbox, base_url_produccion: data.base_url_produccion, timeout: data.timeout, reintentos: data.reintentos });
      setPublicf(data.public || {});
      if (data.entrante && proveedor === "pui") {
        api.get("/integraciones/pui/info").then(({ data: info }) => setPuiInfo(info)).catch(() => {});
      }
    }).catch(() => toast.error("No se pudo cargar la configuración"));
  }, [open, proveedor]);

  const guardar = async () => {
    setSaving(true);
    try {
      const { data: view } = await api.put(`/integraciones/credenciales/${proveedor}`, { ...form, secretos, public: publicf });
      setData(view); setSecretos({});
      toast.success("Configuración guardada · credenciales cifradas");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const probar = async () => {
    setProbando(true); setPrueba(null);
    try {
      const { data: res } = await api.post(`/integraciones/credenciales/${proveedor}/probar`);
      setPrueba(res);
      res.ok ? toast.success(res.mensaje) : toast.error(res.mensaje);
      onSaved && onSaved();
    } catch (e) {
      toast.error("No se pudo probar la conexión");
    } finally { setProbando(false); }
  };

  const onFile = (key) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const buf = await f.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    setSecretos((s) => ({ ...s, [key]: b64 }));
    toast.success(`Archivo ${f.name} cargado`);
  };

  const eb = ESTADO_BADGE[data?.estado] || ESTADO_BADGE.mock;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="cred-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <KeyRound className="h-4 w-4 text-primary" /> {data?.label || "Configurar integración"}
          </DialogTitle>
          <DialogDescription>
            {data ? `${data.auth} · Productos: ${data.productos.join(", ")}${data.firma ? ` · Firma: ${data.firma}` : ""}` : "Cargando configuración…"}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${eb.cls}`}>{eb.label}</span>
              {data.entrante ? (
                <span className="text-xs text-slate-500">Integración ENTRANTE · {data.doc}</span>
              ) : (
                <a href={`https://${(data.doc || "").split(" ")[0]}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-forest-700 hover:underline">
                  Documentación <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            {data.entrante && puiInfo && (
              <div className="space-y-1 rounded-md border border-forest-600/20 bg-forest-600/5 px-3 py-2.5 text-xs text-slate-600" data-testid="pui-urlbase">
                <p><strong className="text-slate-800">FELVORA expone estos endpoints</strong> para que la PUI los consume. URL_BASE de tu institución:</p>
                <code className="block break-all rounded bg-white px-2 py-1 text-forest-700">{puiInfo.url_base}</code>
                <p>Usuario fijo: <strong>{puiInfo.usuario}</strong> · Endpoints: {puiInfo.endpoints.join(", ")}. El RFC (institución) viaja dentro del payload, nunca en la URL.</p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <WithTooltip text="Modo de operación: demo (mock), pruebas (sandbox) o real (producción)">
                  <Label className="mb-1 block text-xs cursor-help underline decoration-dotted">Modo</Label>
                </WithTooltip>
                <Select value={form.modo} onValueChange={(v) => setForm((f) => ({ ...f, modo: v }))}>
                  <SelectTrigger data-testid="cred-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mock">Mock (demo)</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="produccion">Producción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1 block text-xs">Timeout (s)</Label><Input type="number" value={form.timeout} onChange={(e) => setForm((f) => ({ ...f, timeout: e.target.value }))} /></div>
              <div><Label className="mb-1 block text-xs">Reintentos</Label><Input type="number" value={form.reintentos} onChange={(e) => setForm((f) => ({ ...f, reintentos: e.target.value }))} /></div>
            </div>
            {!data.entrante && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="mb-1 block text-xs">URL Sandbox</Label><Input value={form.base_url_sandbox} onChange={(e) => setForm((f) => ({ ...f, base_url_sandbox: e.target.value }))} /></div>
                <div><Label className="mb-1 block text-xs">URL Producción</Label><Input value={form.base_url_produccion} onChange={(e) => setForm((f) => ({ ...f, base_url_produccion: e.target.value }))} /></div>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Credenciales (se guardan cifradas)</p>
              {data.entrante && (
                <p className="text-xs text-amber-700" data-testid="pui-pwd-hint">La contraseña PUI debe tener 16-20 caracteres, con al menos una mayúscula, un número y un carácter especial (! @ # $ % ^ &amp; * ( ) - _ . +).</p>
              )}
              {data.secret_fields.map((f) => (
                <div key={f.key}>
                  <Label className="mb-1 block text-xs">
                    {f.label} {data.secret_mask[f.key] ? <span className="text-slate-400">· actual: {data.secret_mask[f.key]}</span> : null}
                  </Label>
                  {f.long ? (
                    <div className="space-y-1.5">
                      <Textarea data-testid={`cred-${f.key}`} rows={3}
                        placeholder={data.secret_mask[f.key] ? "•••• cargado — deja vacío para conservar" : "Pega el contenido en base64"}
                        value={secretos[f.key] || ""} onChange={(e) => setSecretos((s) => ({ ...s, [f.key]: e.target.value }))} />
                      <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-forest-700 hover:underline">
                        <Upload className="h-3 w-3" /> Subir archivo
                        <input type="file" className="hidden" onChange={onFile(f.key)} />
                      </label>
                    </div>
                  ) : (
                    <Input data-testid={`cred-${f.key}`} type="password"
                      placeholder={data.secret_mask[f.key] ? "•••• deja vacío para conservar" : ""}
                      value={secretos[f.key] || ""} onChange={(e) => setSecretos((s) => ({ ...s, [f.key]: e.target.value }))} />
                  )}
                </div>
              ))}
              {data.public_fields.map((f) => (
                <div key={f.key}>
                  <Label className="mb-1 block text-xs">{f.label}</Label>
                  <Input value={publicf[f.key] || ""} onChange={(e) => setPublicf((p) => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
            </div>

            {prueba && (
              <div data-testid="cred-prueba-result" className={`rounded-md px-3 py-2 text-sm ${prueba.ok ? "bg-forest-600/10 text-forest-700" : "bg-red-50 text-red-700"}`}>
                {prueba.mensaje}
              </div>
            )}

            <p className="text-xs text-slate-400">
              Requiere contrato y credenciales institucionales de la entidad. FELVORA es la plataforma de orquestación;
              la relación con la autoridad o el buró corresponde al sujeto obligado. Sin credenciales, la integración opera en modo demo.
            </p>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={probar} disabled={probando} data-testid="cred-probar">
                {probando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wifi className="mr-1.5 h-4 w-4" />} Probar conexión
              </Button>
              <Button onClick={guardar} disabled={saving} className="bg-primary hover:bg-sidebar-hover" data-testid="cred-guardar">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Guardar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
